import React, { useState, useEffect } from 'react';
import { X, Loader2, Plus, Trash2, Save, Check, Edit2, Palette } from 'lucide-react';
import { User, Tag } from '../../types';
import { getTagsApi } from '../../api/tags';
import request from '../../api/request';

interface TagManagerModalProps {
  user?: User; // Optional user. If provided, it's in "User Selection Mode", else "Global Management Mode"
  onClose: () => void;
  onSave?: (updatedTags: string[]) => void; // Only for User Selection Mode
}

const TagManagerModal: React.FC<TagManagerModalProps> = ({ user, onClose, onSave }) => {
  // Determine mode
  const isUserMode = !!user;

  const [selectedTags, setSelectedTags] = useState<string[]>(user?.tags || []);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Global Management States
  const [isCreating, setIsCreating] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#3B82F6'); // Default blue
  const [isCustomColor, setIsCustomColor] = useState(false);

  const PRESET_COLORS = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#10B981',
    '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'
  ];

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const tags = await getTagsApi();
      setAvailableTags(tags);
    } catch (error) {
      console.error("Failed to fetch tags:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTag = (tagId: string) => {
    if (!isUserMode) return; // In Global Management Mode, clicking might edit or do nothing (or select for deletion if implemented)
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSaveTag = async () => {
    if (!tagName.trim()) return;
    try {
      if (editingTagId) {
        // Update existing tag (assuming backend supports PATCH /api/tags/:id)
        // If not, we might need to delete and create new, but let's try to be optimistic or implement update in backend if missing.
        // The backend provided in context has PATCH /api/tags/:id, so we use it.
        await request.patch(`/api/tags/${editingTagId}`, { name: tagName, color: tagColor });
      } else {
        // Create new
        await request.post('/api/tags', { name: tagName, color: tagColor });
      }
      await fetchTags();
      resetForm();
    } catch (error: any) {
      // 提取后端返回的错误信息
      const errorMessage = error?.response?.data?.detail || error?.message || '保存标签失败';
      console.error("Failed to save tag:", error);
      alert(errorMessage);
    }
  };

  const handleDeleteTag = async (tagId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('确定要删除这个标签吗？此操作将从所有关联用户中移除该标签。')) return;
    try {
      await request.delete(`/api/tags/${tagId}`);
      await fetchTags();
      // Remove from selected if deleted
      setSelectedTags(prev => prev.filter(id => id !== tagId));
    } catch (error) {
      console.error("Failed to delete tag:", error);
    }
  };

  const startEditing = (tag: Tag, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTagId(tag.id);
    setTagName(tag.name);
    setTagColor(tag.color);
    setIsCreating(true);
    setIsCustomColor(!PRESET_COLORS.includes(tag.color));
  };

  const resetForm = () => {
    setTagName('');
    setTagColor('#3B82F6');
    setEditingTagId(null);
    setIsCreating(false);
    setIsCustomColor(false);
  };

  const handleSaveUserTags = () => {
    if (onSave) {
      onSave(selectedTags);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-lg shadow-2xl shadow-primary/20 w-full max-w-md flex flex-col animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">{isUserMode ? '编辑用户标签' : '标签管理'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X />
          </button>
        </header>
        <main className="p-6 space-y-4 min-h-[200px]">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {isUserMode ? `选择要应用于 ${user?.name} 的标签：` : '管理所有可用标签。'}
            </p>
            {!isUserMode && !isCreating && (
              <button
                onClick={() => { resetForm(); setIsCreating(true); }}
                className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-xs flex items-center gap-1 transition-colors"
              >
                <Plus className="w-3 h-3" /> 新建标签
              </button>
            )}
          </div>

          {isCreating && (
            <div className="p-4 border border-border rounded-lg bg-muted/30 space-y-4 animate-in fade-in slide-in-from-top-2">
              <h3 className="text-sm font-semibold">{editingTagId ? '编辑标签' : '创建新标签'}</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  value={tagName}
                  onChange={e => setTagName(e.target.value)}
                  className="w-full p-2 text-sm bg-background border border-border rounded-md focus:ring-2 focus:ring-primary/50 focus:outline-none"
                  placeholder="标签名称"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-muted-foreground">选择颜色</span>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full border border-border shadow-sm" style={{ backgroundColor: tagColor }} />
                    <button
                      onClick={() => setIsCustomColor(!isCustomColor)}
                      className={`p-1.5 rounded-md border ${isCustomColor ? 'bg-accent text-accent-foreground border-accent' : 'bg-background border-border text-muted-foreground'} hover:bg-muted transition-colors`}
                      title="自定义颜色"
                    >
                      <Palette className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isCustomColor ? (
                  <div className="flex items-center gap-2 animate-in fade-in">
                    <input
                      type="color"
                      value={tagColor}
                      onChange={e => setTagColor(e.target.value)}
                      className="w-full h-10 rounded-md cursor-pointer bg-transparent"
                    />
                    <input
                      type="text"
                      value={tagColor}
                      onChange={e => setTagColor(e.target.value)}
                      className="w-24 p-2 text-xs bg-background border border-border rounded-md font-mono"
                    />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {PRESET_COLORS.map(color => (
                      <button
                        key={color}
                        onClick={() => setTagColor(color)}
                        className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${tagColor === color ? 'border-foreground scale-110 shadow-md' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={resetForm} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">取消</button>
                <button
                  onClick={handleSaveTag}
                  disabled={!tagName.trim()}
                  className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1 transition-colors"
                >
                  <Save className="w-3 h-3" /> 保存
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className={`flex flex-col gap-2 max-h-[300px] overflow-y-auto content-start pr-1 ${!isUserMode ? 'mt-4' : ''}`}>
              {availableTags.length === 0 && !isCreating && (
                <p className="text-sm text-muted-foreground w-full text-center py-4">暂无标签，请新建。</p>
              )}
              {availableTags.map(tag => {
                const isSelected = selectedTags.includes(String(tag.id));

                if (isUserMode) {
                  // User Mode: Selectable buttons
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(String(tag.id))}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${isSelected
                          ? 'bg-primary/10 border-primary'
                          : 'bg-card border-border hover:border-primary/50'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: tag.color }} />
                        <span className="font-medium text-sm">{tag.name}</span>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  );
                } else {
                  // Global Management Mode: List with delete/edit options
                  return (
                    <div key={tag.id} className="w-full flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors group">
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full shadow-sm" style={{ backgroundColor: tag.color }} />
                        <span className="font-medium text-sm">{tag.name}</span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => startEditing(tag, e)}
                          className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                          title="编辑标签"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteTag(tag.id, e)}
                          className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                          title="删除标签"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </main>
        <footer className="flex justify-end gap-4 p-4 border-t border-border">
          {isUserMode ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-md text-muted-foreground hover:bg-muted transition-colors">
                取消
              </button>
              <button onClick={handleSaveUserTags} className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                保存更改
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-md bg-muted hover:bg-muted/80 text-foreground transition-colors">
              完成
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default TagManagerModal;
